import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { useEffect } from 'react';

// Hero Section with CTA
const ServiceHero = () => (
  <section className="relative min-h-[500px] flex items-center" data-testid="service-hero">
    <div 
      className="absolute inset-0 bg-cover bg-center"
      style={{
        backgroundImage: `url('https://customer-assets.emergentagent.com/job_cd4f5c4d-92f8-4312-8a77-df80f7c24853/artifacts/mrf246m2_iStock-2155879493.jpg')`
      }}
    />
    <div className="absolute inset-0 bg-[#1a2e44]/80" />
    <div className="relative max-w-6xl mx-auto px-6 lg:px-8 py-20">
      <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 uppercase">
        Professional Pressure Washing
      </h1>
      <p className="text-xl text-slate-300 max-w-2xl mb-8">
        Restoring and maintaining exterior surfaces through professional pressure washing services that protect your property investment.
      </p>
      <Link
        to="/contact?intent=assessment&service=pressure-washing&source=service-pressure-washing"
        className="inline-flex items-center justify-center bg-[#1a2e44] hover:bg-[#0f1d2d] text-white text-sm font-bold py-4 px-8 uppercase tracking-wide transition-colors border border-white/20"
        data-testid="hero-cta-btn"
      >
        Request a Site Assessment
        <ArrowRight className="w-4 h-4 ml-2" />
      </Link>
    </div>
  </section>
);

// What's Included Section
const IncludesSection = () => {
  const services = [
    'Driveway cleaning and restoration',
    'Walkway and sidewalk cleaning',
    'Patio and deck surface cleaning',
    'Pool deck and coping cleaning',
    'Exterior wall and surface cleaning',
    'Stain and contaminant removal'
  ];

  return (
    <section className="bg-white py-20" data-testid="includes-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-[#1a2e44] mb-4">
          What Our Pressure Washing Services Include
        </h2>
        <p className="text-slate-600 mb-10 max-w-2xl">
          Professional cleaning for all exterior hard surfaces:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {services.map((service, index) => (
            <div key={index} className="flex items-start gap-3 p-4 bg-slate-50">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">{service}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why It Matters Section
const WhyItMattersSection = () => (
  <section className="bg-[#1a2e44] py-20" data-testid="why-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <h2 className="text-3xl font-bold text-white mb-6">
        Why Pressure Washing Matters
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="bg-white/10 p-6">
          <h3 className="text-lg font-bold text-white mb-3">Restore Appearance</h3>
          <p className="text-slate-300 text-sm">
            Remove years of dirt, algae, and stains to reveal clean, attractive surfaces that enhance your property.
          </p>
        </div>
        <div className="bg-white/10 p-6">
          <h3 className="text-lg font-bold text-white mb-3">Extend Surface Life</h3>
          <p className="text-slate-300 text-sm">
            Regular cleaning prevents buildup that can cause permanent damage and costly surface replacements.
          </p>
        </div>
        <div className="bg-white/10 p-6">
          <h3 className="text-lg font-bold text-white mb-3">Improve Safety</h3>
          <p className="text-slate-300 text-sm">
            Remove slippery algae and moss from walkways, driveways, and pool decks to reduce slip hazards.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// How We Deliver Section
const DeliverySection = () => (
  <section className="bg-slate-50 py-20" data-testid="delivery-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <h2 className="text-3xl font-bold text-[#1a2e44] mb-10">
        Our Structured Service Process
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 border-t-4 border-t-[#1a2e44]">
          <div className="text-4xl font-bold text-[#1a2e44] mb-3">01</div>
          <h3 className="text-lg font-bold text-[#1a2e44] mb-2">Surface Assessment</h3>
          <p className="text-slate-600 text-sm">
            We evaluate surfaces and determine the appropriate cleaning method.
          </p>
        </div>
        <div className="bg-white p-6 border-t-4 border-t-[#1a2e44]">
          <div className="text-4xl font-bold text-[#1a2e44] mb-3">02</div>
          <h3 className="text-lg font-bold text-[#1a2e44] mb-2">Pre-Treatment</h3>
          <p className="text-slate-600 text-sm">
            We apply appropriate treatments for stubborn stains and buildup.
          </p>
        </div>
        <div className="bg-white p-6 border-t-4 border-t-[#1a2e44]">
          <div className="text-4xl font-bold text-[#1a2e44] mb-3">03</div>
          <h3 className="text-lg font-bold text-[#1a2e44] mb-2">Professional Cleaning</h3>
          <p className="text-slate-600 text-sm">
            Our trained technicians perform thorough surface cleaning.
          </p>
        </div>
        <div className="bg-white p-6 border-t-4 border-t-[#1a2e44]">
          <div className="text-4xl font-bold text-[#1a2e44] mb-3">04</div>
          <h3 className="text-lg font-bold text-[#1a2e44] mb-2">Quality Review</h3>
          <p className="text-slate-600 text-sm">
            We inspect results and document the completed work.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Documentation Section
const DocumentationSection = () => (
  <section className="bg-white py-20" data-testid="documentation-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="text-3xl font-bold text-[#1a2e44] mb-6">
            Documentation & Reporting
          </h2>
          <p className="text-slate-600 mb-8">
            Our pressure washing services include complete documentation:
          </p>
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">Before-and-after photos</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">Service completion confirmation</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">Surface condition observations</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">Recommended maintenance frequency</span>
            </li>
          </ul>
        </div>
        <div className="bg-slate-50 p-8 border border-slate-200">
          <p className="text-slate-600 italic text-lg mb-4">
            "Our documentation helps you track surface condition over time and plan future maintenance."
          </p>
          <p className="text-slate-700 text-sm">
            Routine surface cleaning helps preserve stone, concrete, and hardscape materials over time.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Service Area Section
const ServiceAreaSection = () => (
  <section className="bg-slate-50 py-16" data-testid="service-area-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
      <h2 className="text-2xl font-bold text-[#1a2e44] mb-4">
        Service Area
      </h2>
      <p className="text-slate-600 max-w-2xl mx-auto">
        Serving Pasadena, Arcadia, San Marino, La Cañada Flintridge, and communities throughout Los Angeles County.
      </p>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section className="bg-[#1a2e44] py-20" data-testid="cta-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
      <h2 className="text-3xl font-bold text-white mb-4">
        Schedule a Property Assessment
      </h2>
      <p className="text-slate-400 mb-8 max-w-xl mx-auto">
        Schedule a property assessment to determine the appropriate surface cleaning and maintenance schedule.
      </p>
      <Link
        to="/contact?intent=assessment&service=pressure-washing&source=service-pressure-washing"
        className="inline-flex items-center justify-center bg-[#1a2e44] hover:bg-[#0f1d2d] text-white text-sm font-bold py-4 px-8 uppercase tracking-wide transition-colors border border-white/20"
        data-testid="cta-btn"
      >
        Request a Site Assessment
        <ArrowRight className="w-4 h-4 ml-2" />
      </Link>
    </div>
  </section>
);

export default function PressureWashingPage() {
  useEffect(() => {
    document.title = 'Pressure Washing | Sunlight Grounds Care';
  }, []);

  return (
    <div data-testid="pressure-washing-page">
      <ServiceHero />
      <IncludesSection />
      <WhyItMattersSection />
      <DeliverySection />
      <DocumentationSection />
      <CTASection />
      <ServiceAreaSection />
    </div>
  );
}
